import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import qs from 'qs';
import {
  AppBar,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  Slide,
  Tab,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { FilterOptionsState, TabContext, TabList, TabPanel } from '@material-ui/lab';
import {
  Apartment,
  Assignment,
  Close,
  Info,
  MessageOutlined,
  RecordVoiceOver,
  Sms,
  TrendingDown,
} from '@material-ui/icons';
import { TransitionProps } from '@material-ui/core/transitions';
import { AxiosError } from 'axios';
import { deburr } from 'lodash';
import { checkRealEstateEvents } from 'api';
import { path as PageHomePath } from 'pages/PageHome';
import { getApplicationAvailableGroupColumns, getApplications } from 'pages/PageApplicationList/api';
import UsoDebtDialog from 'pages/PageApplicationNew/components/UsoDebtDialog';
import RealEstateInfo from 'pages/PageApplicationNew/components/RealEstateInfo';
import { getPath as getApplicationPath } from 'pages/PageApplication';
import { checkIsPhoneOfTenant, checkIsPhoneRegisteredInBigApp } from 'pages/PageApplicationNew/api';
import { IPhoneInfo, IRoomDiscount } from 'pages/PageApplicationNew/interfaces';
import { checkApplicationAccidents } from 'pages/PageApplicationNew/components/ApplicationAccidentDialog/api';
import ClientCard from 'components/ClientCard';
import ClientInsightCard from 'components/ClientInsightCard';
import { useClientProfile } from 'components/ClientInsightCard/useClientProfile';
import Consulting from 'components/Consulting';
import Container from 'components/Container';
import DynamicTable, { IDynamicTableParams } from 'components/DynamicTable';
import { SelectRefactor } from 'components/OldSelect';
import PageHeader from 'components/PageHeader';
import TextField from 'components/TextField';
import ErrorBlock from 'components/ErrorBlock';
import useError from 'components/ErrorProvider/useError';
import { detectMaskByPrefix, excludePrefixFromPhoneNumber } from 'components/TelInput/helpers';
import DiscountsDialogs from 'dialogs/DiscountsDialogs';
import SendBigAppDownloadLinkDialog from 'dialogs/SendBigAppDownloadLinkDialog';
import { BigAppDialog } from 'pages/PageHome/components';
import BIServiceCRM from 'assets/icons/svg/BIServiceCRM';
import BI from 'assets/icons/svg/BI';
import BigApp from 'assets/icons/svg/BigApp';
import {
  APPLICATION_DEFAULT_COLUMNS,
  APPLICATION_DEFAULT_FILTERS,
  cleanNumberMask,
  clearPhoneNumberFormat,
  numberMask,
  USER_OPERATIONS,
} from 'helper';
import {
  IApplicationSingleModel,
  IAvailableColumnModel,
  IFilterColumnModel,
  IOrderColumnModel,
  IOwnerModel,
  IOwnerRoomModel,
  IRealEstateModel,
  IRoomModel,
  RootState,
} from 'interfaces';
import {
  checkIfHasPublicApplications,
  getOwnerRooms,
  getRealEstatesShort,
  getRoomAllDiscounts,
  loadRealEstate,
} from './api';
import { RoomField } from './components';
import { handleClickHasPublicApplicationButton } from './helpers';
import './styles.scss';

const useStyles = makeStyles({
  root: {
    padding: 20,

    '& .page-header': {
      margin: '0 0 20px',
    },

    '& .dynamic-table': {
      // Таблица теперь внутри таба — старый отрицательный отступ контролов
      // под прежнюю раскладку не нужен (иначе кнопки наезжают на панель табов).
      '&-controls': {
        margin: 0,
      },
      '&-area': {
        height: 'calc(100vh - 680px)',
      },
    },
  },
});

const STORAGE_KEY = 'applications-by-phone';

interface IInfoByPhoneDialogProps {
  open: boolean;
  callChainId?: string | null;
  incomingPhoneNumber?: string;
  phonePrefix?: string;
  onClose: () => void;
}

const InfoByPhoneDialog: React.FC<IInfoByPhoneDialogProps> = ({ open, onClose, ...rest }) => {
  const operations = useSelector((state: RootState) => state.operations);
  const hasClientProfileAi = operations.includes(USER_OPERATIONS.CLIENT_PROFILE_AI);
  const hasClientProfileAiManager = operations.includes(USER_OPERATIONS.CLIENT_PROFILE_AI_MANAGER);
  const hasAnyClientProfileAi = hasClientProfileAi || hasClientProfileAiManager;

  // Local state
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(rest.incomingPhoneNumber ?? '');
  const [phonePrefix, setPhonePrefix] = useState(rest.phonePrefix ?? '+7');
  const [userName, setUserName] = useState('');
  const [realEstate, setRealEstate] = useState<IRealEstateModel | null>(null);
  const [room, setRoom] = useState<IRoomModel | null>(null);
  const [roomOwner, setRoomOwner] = useState<IOwnerModel | null>(null);
  const [realEstates, setRealEstates] = useState<IRealEstateModel[]>([]);
  const [clientRealEstates, setClientRealEstates] = useState<IRealEstateModel[]>([]);
  const [clientRooms, setClientRooms] = useState<IOwnerRoomModel[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [roomDiscounts, setRoomDiscounts] = useState<IRoomDiscount[]>([]);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSyncing, setSyncing] = useState(false);
  const [isEventsBadgeVisible, setIsEventsBadgeVisible] = useState(false);
  const [isAccidentsBadgeVisible, setIsAccidentsBadgeVisible] = useState(false);
  const [hasPublicApplications, setHasPublicApplications] = useState(false);
  const [contactPhoneIsRegisitedInBigApp, setContactPhoneIsRegisitedInBigApp] = useState<IPhoneInfo | null>(null);
  const [contactPhoneIsOfTenant, setContactPhoneIsOfTenant] = useState<IPhoneInfo | null>(null);
  const [realEstateInfoDialogOpened, setOpenedRealEstateInfoDialog] = useState(false);
  const [bigAppMarketplaceDialogOpened, setOpenedBigAppMarketplaceDialog] = useState(false);
  const [usoDebtDialogOpened, setOpenedUsoDebtDialog] = useState(false);
  const [discountsDialogOpened, setOpenedDiscountsDialog] = useState(false);
  const [sendBigAppDowloadLinkDialogOpened, setOpenedSendBigAppDowloadLink] = useState(false);
  const [bottomTab, setBottomTab] = useState<'applications' | 'card'>('applications');

  // ИИ-подсказка о клиенте (профиль по номеру телефона) — отдельный долгий запрос.
  // queriedPhone выставляется при загрузке номера (getData); хук тянет обе карточки одним запросом.
  const [queriedPhone, setQueriedPhone] = useState('');
  const {
    loading: clientInsightLoading,
    error: clientInsightError,
    operator: operatorInsight,
    manager: managerInsight,
    reload: reloadClientInsight,
  } = useClientProfile(queriedPhone, hasAnyClientProfileAi);
  // Имя в шапке AI-карточек: ФИО (если уже подгружено) либо номер телефона.
  const clientInsightName =
    userName ||
    numberMask(clearPhoneNumberFormat(queriedPhone, phonePrefix), `${phonePrefix} ${detectMaskByPrefix(phonePrefix)}`);

  const DEFAULT_FILTER_VALUES: {
    visibleColumns: IAvailableColumnModel[];
    visibleFilters: IAvailableColumnModel[];
  } | null = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');

  // Styles
  const classes = useStyles();

  // Global notification
  const { addError } = useError();

  // DynamicTable props
  const visibleColumns = (DEFAULT_FILTER_VALUES?.visibleColumns || APPLICATION_DEFAULT_COLUMNS).filter((column) =>
    [
      'applicationNumber',
      'createDate',
      'realEstateName',
      'defectTypeName',
      'statusName',
      'contactName',
      'contactPhone',
      'isDelay',
    ].includes(column.columnName),
  );
  const visibleFilters = (DEFAULT_FILTER_VALUES?.visibleFilters || APPLICATION_DEFAULT_FILTERS).filter((column) =>
    [
      'applicationNumber',
      'createDate',
      'realEstateGuid',
      'defectTypeId',
      'statusId',
      'contactName',
      'contactPhone',
      'isDelay',
    ].includes(column.columnName),
  );

  const isGone = useRef(false);

  // Префикс приходит аргументом: getData — useCallback с пустыми deps, а при входящем звонке
  // он вызывается сразу за setPhonePrefix, когда новое состояние ещё не применилось
  const getData = useCallback((incomingPhoneNumber: string, prefix: string): void => {
    setLoading(true);
    setLoaded(false);
    setUserName('');
    setRealEstate(null);
    setRoom(null);
    setQueriedPhone(incomingPhoneNumber);

    Promise.all([
      // В room_owner номер хранится с кодом страны, поиск точный — отправляем полный номер
      getOwnerRooms(`${prefix}${clearPhoneNumberFormat(incomingPhoneNumber, prefix)}`),
      getRealEstatesShort({ onlyFromMatrix: true, nameWithCity: true, searchWithCity: true }),
    ])
      .then((response) => {
        if (Boolean(response[0].length)) {
          setClientRooms([...response[0]]);

          const realEstates = response[0].map((roomOwner) => roomOwner.realEstateGuid);
          const clientRealEstates = response[1].filter((realEstate) => realEstates.includes(realEstate.guid));
          const realEstate = clientRealEstates.filter(
            (realEstate) => realEstate.guid === response[0][0].realEstateGuid,
          )[0];

          setClientRealEstates(clientRealEstates);
          setRoom({
            guid: response[0][0].roomGuid,
            name: response[0][0].roomName,
            contract: response[0][0].contractGuid,
          });

          loadRealEstate(realEstate.guid).then(setRealEstate);

          setRealEstates(response[1].filter((realEstate) => !realEstates.includes(realEstate.guid)));

          if (realEstate) {
            checkRealEstateEvents(realEstate.guid)
              .then((response) => setIsEventsBadgeVisible(response.hasEvent))
              .catch((error) => {
                addError(error.response.data.message);
              });

            checkApplicationAccidents(realEstate.guid)
              .then((response) => setIsAccidentsBadgeVisible(response.hasAccident))
              .catch((error) => {
                addError(error.response?.data.message);
              });
          }

          handleCheckIfHasPublicApplications(realEstate);
        } else {
          setIsEventsBadgeVisible(false);
          setHasPublicApplications(false);
          setIsAccidentsBadgeVisible(false);
          setRealEstates(response[1]);
        }

        checkPhoneInfo(incomingPhoneNumber, prefix);
        setLoaded(true);
        setError(false);
      })
      .catch((error) => {
        setError(true);
        setErrorMessage(error.response?.data.message);
        setLoaded(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []); // eslint-disable-line

  useEffect(() => {
    isGone.current = false;

    if (!isGone.current) {
      if (!open) {
        setLoading(false);
        setLoaded(false);
        setPhoneNumber('');
        setPhonePrefix('+7');
        setUserName('');
        setRealEstate(null);
        setRoom(null);
        setRealEstates([]);
        setClientRealEstates([]);
        setClientRooms([]);
        setQueriedPhone('');
      }

      // Диалог не размонтируется между открытиями, а префикс приходит и без номера:
      // у оператора УЗ это +998, и поле должно открыться с ним даже когда звонка нет
      setPhonePrefix(rest.phonePrefix ?? '+7');

      if (rest.incomingPhoneNumber) {
        setPhoneNumber(rest.incomingPhoneNumber);
        getData(rest.incomingPhoneNumber, rest.phonePrefix ?? '+7');
      }

      setIsEventsBadgeVisible(false);
      setHasPublicApplications(false);
      setIsAccidentsBadgeVisible(false);
    }

    return () => {
      isGone.current = true;
    };
  }, [open, rest.incomingPhoneNumber, rest.phonePrefix, getData]);

  useEffect(() => {
    if (roomOwner) {
      const isPhoneOfTenant =
        roomOwner.tenantPhone &&
        cleanNumberMask(phoneNumber) === cleanNumberMask(excludePrefixFromPhoneNumber(roomOwner.tenantPhone));

      setUserName(isPhoneOfTenant ? String(roomOwner.tenantName) : roomOwner.fullName);
    } else setUserName('');
  }, [roomOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!room) return;

    (async () => {
      try {
        setRoomDiscounts(await getRoomAllDiscounts(room.guid));
      } catch (error) {
        addError((error as AxiosError).response!.data.message);
      }
    })();
  }, [room]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkPhoneInfo = (incomingPhoneNumber: string, prefix: string) => {
    const digits = clearPhoneNumberFormat(incomingPhoneNumber, prefix);
    const digitsCount = (detectMaskByPrefix(prefix).match(/#/g) ?? []).length; // 10 для КЗ, 9 для УЗ

    // Big App и аренда есть только в казахстанском контуре
    if (prefix !== '+7' || digits.length !== digitsCount) {
      setContactPhoneIsRegisitedInBigApp(null);
      setContactPhoneIsOfTenant(null);
      return;
    }

    const phone = numberMask(digits, `${prefix}${'#'.repeat(digitsCount)}`);

    checkIsPhoneRegisteredInBigApp(phone)
      .then(setContactPhoneIsRegisitedInBigApp)
      .catch(() => addError('Не удалось получить информацию по номеру телефона'));

    checkIsPhoneOfTenant(phone)
      .then(setContactPhoneIsOfTenant)
      .catch(() => addError('Не удалось получить информацию по номеру телефона'));
  };

  const onChangeRealEstate = (realEstate: IRealEstateModel | null): void => {
    if (!realEstate) return;

    loadRealEstate(realEstate.guid).then(setRealEstate);

    checkRealEstateEvents(realEstate.guid)
      .then((response) => setIsEventsBadgeVisible(response.hasEvent))
      .catch((error) => {
        addError(error.response.data.message);
      });

    checkApplicationAccidents(realEstate.guid)
      .then((response) => setIsAccidentsBadgeVisible(response.hasAccident))
      .catch((error) => {
        addError(error.response?.data.message);
      });

    handleCheckIfHasPublicApplications(realEstate);

    setRoom(null);
  };

  const handleCheckIfHasPublicApplications = (realEstate: IRealEstateModel | null) => {
    if (!realEstate) return;

    if (operations.includes(USER_OPERATIONS.VIEW_APP_IS_PUBLIC_OPERATION))
      checkIfHasPublicApplications({ params: { realEstateGuid: realEstate.guid } })
        .then(({ hasApplicationIsPublic }) => setHasPublicApplications(hasApplicationIsPublic))
        .catch((error) => {
          addError(error.response.data.message);
        });
  };

  const onClickCreateApplication = (): void => {
    const queryData: {
      incomeNumber: string;
      phonePrefix: string;
      realEstateGuid?: string;
      roomGuid?: string;
      userName?: string;
      tenantPhone?: string;
      callChainId?: string;
    } = {
      incomeNumber: clearPhoneNumberFormat(phoneNumber, phonePrefix),
      // Без префикса форма заявки не знает контур и разбирает номер по казахстанской маске
      phonePrefix,
    };

    if (realEstate) queryData.realEstateGuid = realEstate.guid;
    if (room) queryData.roomGuid = room.guid;
    if (userName) queryData.userName = userName;
    if (rest.callChainId) queryData.callChainId = rest.callChainId;

    window.open(`${window.location.origin}/application/new${qs.stringify(queryData, { addQueryPrefix: true })}`);
  };

  const getItems = (data: IDynamicTableParams): Promise<any[]> => {
    if (data.filterColumns)
      data.filterColumns.push({
        columnName: 'contactPhone',
        criteria: 'CONTAINS_IGNORE_CASE',
        filterValues: [`${phonePrefix}${clearPhoneNumberFormat(phoneNumber, phonePrefix)}`],
      });

    return new Promise((resolve, reject) => {
      getApplications(data).then(resolve).catch(reject);
    });
  };

  const handleChangeDynamicTable = (
    applications: any[],
    visibleColumns: IAvailableColumnModel[],
    visibleFilters: IAvailableColumnModel[],
    filterColumns: IFilterColumnModel[],
    orderColumns: IOrderColumnModel[],
    pageNo: number,
    pageSize: number,
  ) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ visibleColumns, visibleFilters, filterColumns, orderColumns, pageNo, pageSize }),
    );
  };

  const renderRealEstateOption = (item: IRealEstateModel): React.ReactNode => {
    return (
      <div>
        <p style={{ margin: 0 }}>{item.name}</p>

        {/* FIXME: addressRu - is a temp field. Need to replace to address */}
        {item.addressRu && item.addressRu.length > 0 && (
          <p style={{ margin: 0, color: ' rgba(0, 0, 0, 0.5)' }}>
            <small>{item.addressRu}</small>
          </p>
        )}
      </div>
    );
  };

  const filterRealEstateOptions = (
    options: IRealEstateModel[],
    { inputValue }: FilterOptionsState<IRealEstateModel>,
  ): IRealEstateModel[] => {
    const searchValue: string = inputValue === null ? '' : deburr(inputValue.trim()).toLowerCase();

    return options.filter(
      (option) =>
        // FIXME: addressRu - is a temp field. Need to replace to address
        option?.name!.toLowerCase().indexOf(searchValue) > -1 ||
        option?.addressRu!.toLowerCase().indexOf(searchValue) > -1,
    );
  };

  const FIXED_COL = {
    caption: 'Количество оставшихся дней',
    columnName: 'restOfDay',
    dictionaryMap: null,
    filterCaption: 'Количество оставшихся дней',
    filterCriteriaList: [],
    type: 'Integer',
  };

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={SlideTransition}
        PaperProps={{ style: { backgroundColor: '#f5f5f5' } }}
        disableEnforceFocus
        fullScreen
        className="info-by-phone-dialog"
      >
        <AppBar position="relative">
          <Toolbar>
            <Button className="app-bar-logo" component={NavLink} to={PageHomePath} onClick={onClose}>
              <BIServiceCRM />
            </Button>

            <Box marginLeft="auto">
              <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                <Close />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Box className={classes.root}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Container>
                <PageHeader title="Журнал звонка" />

                <Box sx={{ mb: 2.5 }}>
                  <ButtonGroup>
                    <Button
                      startIcon={<Assignment color="primary" />}
                      onClick={onClickCreateApplication}
                      disabled={!loaded}
                    >
                      Создать заявку
                    </Button>

                    <Button
                      onClick={() => setOpenedUsoDebtDialog(true)}
                      startIcon={<Info style={{ color: '#ffcb2e' }} />}
                      disabled={!room || !roomOwner}
                    >
                      Долг по УСО
                    </Button>

                    <Button
                      disabled={!realEstate}
                      onClick={() => setOpenedRealEstateInfoDialog(true)}
                      startIcon={<Apartment style={{ color: '#27ae60' }} />}
                    >
                      {isEventsBadgeVisible ? (
                        <Badge classes={{ badge: 'button-group__button-badge' }} color="secondary" badgeContent=" ">
                          Карточка ЖК
                        </Badge>
                      ) : (
                        'Карточка ЖК'
                      )}
                    </Button>

                    {contactPhoneIsRegisitedInBigApp?.isRegisteredInBigApp && (
                      <Button startIcon={<BI width={20} height={20} />}>Зарегистрирован BIG APP</Button>
                    )}

                    {contactPhoneIsRegisitedInBigApp?.isHater &&
                      operations.includes(USER_OPERATIONS.TENANT_VIEW_OPERATION) && (
                        <Button startIcon={<RecordVoiceOver color="primary" />}>Хейтер</Button>
                      )}

                    {Boolean(roomDiscounts.length) && (
                      <Button
                        startIcon={<TrendingDown color="primary" />}
                        onClick={() => setOpenedDiscountsDialog(true)}
                      >
                        Скидка
                      </Button>
                    )}

                    {operations.includes(USER_OPERATIONS.VIEW_APP_IS_PUBLIC_OPERATION) && (
                      <Button
                        component="a"
                        href="/application/list"
                        target="_blank"
                        onClick={() =>
                          handleClickHasPublicApplicationButton({
                            realEstateGuid: realEstate?.guid,
                            realEstateName: realEstate?.name,
                          })
                        }
                        disabled={!realEstate}
                        startIcon={<MessageOutlined color="secondary" />}
                      >
                        {hasPublicApplications ? (
                          <Badge classes={{ badge: 'button-group__button-badge' }} color="secondary" badgeContent=" ">
                            Публичные заявки
                          </Badge>
                        ) : (
                          'Публичные заявки'
                        )}
                      </Button>
                    )}

                    {operations.includes(USER_OPERATIONS.SEND_BIG_APP_LINK) && phonePrefix === '+7' && (
                      <Button startIcon={<Sms color="primary" />} onClick={() => setOpenedSendBigAppDowloadLink(true)}>
                        BIG App
                      </Button>
                    )}

                    {operations.includes(USER_OPERATIONS.MANAGE_BIG_APP_MARKETPLACE) && (
                      <Button
                        startIcon={<BigApp width={24} height={24} />}
                        onClick={() => setOpenedBigAppMarketplaceDialog(true)}
                      >
                        Платные заявки
                      </Button>
                    )}
                  </ButtonGroup>
                </Box>

                {contactPhoneIsOfTenant?.isTenant && (
                  <Box sx={{ mb: 2.5, display: 'inline-flex', bgcolor: '#DFF0D8', borderRadius: 8, py: 1, px: 2 }}>
                    <Typography>
                      <b>Арендатор</b>
                    </Typography>
                  </Box>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <Box sx={{ bgcolor: '#FFF', p: 2.5, borderRadius: 8 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={5}>
                          <TextField
                            label="Входящий звонок"
                            mask={detectMaskByPrefix(phonePrefix)}
                            onKeyPress={(event) => event.key === 'Enter' && getData(phoneNumber, phonePrefix)}
                            fullWidth
                            disabled={loading}
                            value={numberMask(
                              clearPhoneNumberFormat(phoneNumber, phonePrefix),
                              detectMaskByPrefix(phonePrefix),
                            )}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">{phonePrefix}</InputAdornment>,
                            }}
                            onChange={(event) => setPhoneNumber(event.target.value)}
                          />
                        </Grid>

                        <Grid item xs={7}>
                          <TextField
                            label="ФИО"
                            fullWidth
                            disabled={loading}
                            value={userName}
                            onChange={(event) => setUserName(event.target.value)}
                          />
                        </Grid>

                        <Grid item xs={7}>
                          <SelectRefactor
                            label="Выбрать ЖК*"
                            value={realEstate}
                            loading={loading}
                            groupBy={(option) => option.groupName}
                            getOptionLabel={(item: IRealEstateModel) => String(item.name)}
                            onChange={onChangeRealEstate}
                            options={[
                              ...clientRealEstates.map((item) => ({ ...item, groupName: 'ЖК клиента' })),
                              ...realEstates.map((item) => ({ ...item, groupName: 'Другие' })),
                            ]}
                            filterOptions={filterRealEstateOptions}
                            renderOption={renderRealEstateOption}
                            disabled={loading}
                            optionDefaultLabel="name"
                          />
                        </Grid>

                        <Grid item xs={5}>
                          <RoomField
                            realEstateGuid={realEstate ? realEstate.guid! : ''}
                            setRoom={setRoom}
                            isSyncing={isSyncing}
                            setRoomOwner={setRoomOwner}
                            disabled={loading}
                            clientRooms={clientRooms}
                            room={room}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  <Grid item xs={4}>
                    <Consulting
                      realEstateGuid={loaded && realEstate ? realEstate.guid! : ''}
                      incomePhone={loaded ? phoneNumber : ''}
                      contactName={userName}
                      callChainId={rest.callChainId ? rest.callChainId : ''}
                      roomGuid={loaded && room ? room.guid : ''}
                    />
                  </Grid>
                </Grid>

                {hasClientProfileAi && (clientInsightLoading || clientInsightError || operatorInsight) && (
                  <Box sx={{ mt: 2.5 }}>
                    <ClientInsightCard
                      loading={clientInsightLoading}
                      error={clientInsightError}
                      onRetry={reloadClientInsight}
                      data={operatorInsight ? { ...operatorInsight, clientName: clientInsightName } : undefined}
                    />
                  </Box>
                )}

                {hasClientProfileAiManager && (clientInsightLoading || clientInsightError || managerInsight) && (
                  <Box sx={{ mt: 2.5 }}>
                    <ClientInsightCard
                      loading={clientInsightLoading}
                      error={clientInsightError}
                      onRetry={reloadClientInsight}
                      data={managerInsight ? { ...managerInsight, clientName: clientInsightName } : undefined}
                    />
                  </Box>
                )}

                {!loading && error && (
                  <Box sx={{ my: 2 }}>
                    <ErrorBlock
                      retry={() => getData(phoneNumber, phonePrefix)}
                      box={false}
                      message={errorMessage ?? ''}
                    />
                  </Box>
                )}
              </Container>
            </Grid>

            {loaded && !loading && (
              <Grid item xs={12}>
                <Box sx={{ pt: 5 }}>
                  <Container>
                    <Box sx={{ bgcolor: '#FFF', borderRadius: 8, px: 2.5, py: 1 }}>
                      <TabContext value={bottomTab}>
                        <TabList
                          onChange={(_event, value) => setBottomTab(value)}
                          indicatorColor="primary"
                          textColor="primary"
                        >
                          {[
                            <Tab label="Заявки" value="applications" key="applications" />,
                            operations.includes(USER_OPERATIONS.CLIENT_CARD_INFO) ? (
                              <Tab label="Карточка клиента" value="card" key="card" />
                            ) : null,
                          ].filter(Boolean)}
                        </TabList>

                        <TabPanel value="applications" style={{ padding: '16px 0 0' }}>
                          <DynamicTable
                            pageNo={1}
                            pageSize={20}
                            fixedColumn={FIXED_COL}
                            requiredFields={['applicationNumber']}
                            getItems={getItems}
                            getAvailableGroupColumns={getApplicationAvailableGroupColumns}
                            filterColumns={[
                              {
                                columnName: 'contactPhone',
                                criteria: 'IN',
                                // Восьмёрочный формат хранения есть только у казахстанских номеров
                                filterValues:
                                  phonePrefix === '+7'
                                    ? [`8${clearPhoneNumberFormat(phoneNumber, '+7')}`]
                                    : [`${phonePrefix}${clearPhoneNumberFormat(phoneNumber, phonePrefix)}`],
                              },
                            ]}
                            onChange={handleChangeDynamicTable}
                            orderColumns={[{ columnName: 'applicationNumber', sortType: 'DESC' }]}
                            defaultValues={{
                              columns: DEFAULT_FILTER_VALUES?.visibleColumns || visibleColumns,
                              filters: DEFAULT_FILTER_VALUES?.visibleFilters || visibleFilters,
                            }}
                            visibleColumns={DEFAULT_FILTER_VALUES?.visibleColumns || visibleColumns}
                            visibleFilters={DEFAULT_FILTER_VALUES?.visibleFilters || visibleFilters}
                            setItemLink={(item: IApplicationSingleModel): string =>
                              getApplicationPath(item.applicationNumber)
                            }
                            filterConfigName={STORAGE_KEY}
                            canReset
                          />
                        </TabPanel>

                        {operations.includes(USER_OPERATIONS.CLIENT_CARD_INFO) && (
                          <TabPanel value="card" style={{ padding: '16px 0 0' }}>
                            {/* помещение с владельцем передаём, чтобы в карточке была видна ДЗ по нему */}
                            <ClientCard
                              phone={phoneNumber}
                              roomGuid={room?.guid}
                              ownerGuid1c={roomOwner?.ownerGuid1c}
                            />
                          </TabPanel>
                        )}
                      </TabContext>
                    </Box>
                  </Container>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Dialog>

      {realEstate && (
        <RealEstateInfo
          open={realEstateInfoDialogOpened}
          onSyncRooms={() => setSyncing(true)}
          isEventsBadgeVisible={isEventsBadgeVisible}
          isAccidentsBadgeVisible={isAccidentsBadgeVisible}
          onClose={() => setOpenedRealEstateInfoDialog(false)}
          realEstate={realEstate}
        />
      )}

      {room?.guid && roomOwner?.ownerGuid1c && (
        <UsoDebtDialog
          open={usoDebtDialogOpened}
          kontragentGuid={roomOwner.ownerGuid1c}
          roomGuid={room.guid}
          realEstateGuid={loaded && realEstate ? realEstate.guid : undefined}
          contractGuid={room.contract || undefined}
          kontragentName={roomOwner.fullName}
          roomName={room.name}
          onClose={() => setOpenedUsoDebtDialog(false)}
        />
      )}

      <DiscountsDialogs
        open={discountsDialogOpened}
        title="Скидки"
        onClose={() => setOpenedDiscountsDialog(false)}
        discounts={roomDiscounts}
      />

      <SendBigAppDownloadLinkDialog
        open={sendBigAppDowloadLinkDialogOpened}
        realEstate={realEstate}
        phoneNumber={numberMask(clearPhoneNumberFormat(phoneNumber, phonePrefix), '(###) ###-##-##')}
        onClose={() => setOpenedSendBigAppDowloadLink(false)}
      />

      <BigAppDialog open={bigAppMarketplaceDialogOpened} onClose={() => setOpenedBigAppMarketplaceDialog(false)} />
    </>
  );
};

const SlideTransition = React.forwardRef((props: TransitionProps, ref) => {
  return <Slide {...props} ref={ref} direction="up" />;
});

export default InfoByPhoneDialog;
